/**
 * Discount Kit Bundle Selector UI enhancer.
 * Targets <bundle-selector> shadow DOM (DK renders .dk-bundles inside shadowRoot).
 */
(function () {
  const HOST_SELECTOR = 'bundle-selector';
  const SELECT_SELECTOR = '.dk-bundle-slot-selector-wrapper select';
  const DATA_ATTR = 'data-dk-buttons-added';
  const HOST_INIT_ATTR = 'data-dk-enhancer-init';
  const STYLE_ATTR = 'data-dk-enhancer-style';
  const ATC_PROXY_ATTR = 'data-dk-atc-proxy';

  /** @type {Map<ShadowRoot, HTMLElement>} */
  const shadowHostMap = new Map();

  /** @type {WeakSet<ShadowRoot>} */
  const initializedShadows = new WeakSet();

  /** @type {WeakMap<ShadowRoot, boolean>} */
  const isEnhancingMap = new WeakMap();

  let cachedStyles = null;
  let cachedStylesUrl = null;
  /** @type {object|null|undefined} */
  let cachedEnhancerConfig;
  let atcProxyInitialized = false;

  const DK_STYLESHEET_LOAD_TIMEOUT_MS = 3000;

  /**
   * @returns {object|null}
   */
  function getEnhancerConfig() {
    if (cachedEnhancerConfig !== undefined) return cachedEnhancerConfig;

    const configEl = document.querySelector('[data-dk-bundle-enhancer-config]');
    if (!configEl) {
      cachedEnhancerConfig = null;
      return null;
    }

    try {
      cachedEnhancerConfig = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('Discount Kit enhancer: failed to parse config', error);
      cachedEnhancerConfig = null;
    }

    return cachedEnhancerConfig;
  }

  /**
   * @param {string} optionsString
   * @returns {number[]}
   */
  function parseOptionIndexes(optionsString) {
    if (!optionsString || typeof optionsString !== 'string') return [];

    const seen = new Set();
    const indexes = [];

    optionsString.split(',').forEach((part) => {
      const index = parseInt(part.trim(), 10);
      if (index > 0 && !seen.has(index)) {
        seen.add(index);
        indexes.push(index);
      }
    });

    return indexes;
  }

  /**
   * @param {ShadowRoot} shadowRoot
   */
  async function waitForDkStylesheet(shadowRoot) {
    const link = shadowRoot.querySelector('link[rel="stylesheet"]');
    if (!link) return;

    if (link.sheet) return;

    await new Promise((resolve) => {
      const timeoutId = window.setTimeout(resolve, DK_STYLESHEET_LOAD_TIMEOUT_MS);

      link.addEventListener(
        'load',
        () => {
          window.clearTimeout(timeoutId);
          resolve();
        },
        { once: true },
      );

      link.addEventListener(
        'error',
        () => {
          window.clearTimeout(timeoutId);
          resolve();
        },
        { once: true },
      );
    });
  }

  /**
   * @param {HTMLElement|null|undefined} el
   * @param {string} value
   */
  function setTextIfChanged(el, value) {
    if (!el || el.textContent === value) return;
    el.textContent = value;
  }

  /**
   * @param {ShadowRoot} shadow
   * @param {HTMLElement} host
   * @param {() => void} fn
   */
  function runEnhancement(shadow, host, fn) {
    if (isEnhancingMap.get(shadow)) return;

    isEnhancingMap.set(shadow, true);
    try {
      fn();
    } finally {
      isEnhancingMap.set(shadow, false);
    }
  }

  /**
   * @param {ShadowRoot} shadowRoot
   */
  async function injectStyles(shadowRoot) {
    await waitForDkStylesheet(shadowRoot);

    const stylesheetUrl = document.querySelector('link[href*="dk-bundle-enhancer.css"]')?.href;
    if (!stylesheetUrl) {
      console.warn('Discount Kit enhancer: dk-bundle-enhancer.css link not found');
      return;
    }

    if (!cachedStyles || cachedStylesUrl !== stylesheetUrl) {
      try {
        const response = await fetch(stylesheetUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        cachedStyles = await response.text();
        cachedStylesUrl = stylesheetUrl;
      } catch (error) {
        console.error('Discount Kit enhancer: failed to fetch styles', error);
        return;
      }
    }

    let styleEl = shadowRoot.querySelector(`[${STYLE_ATTR}]`);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.setAttribute(STYLE_ATTR, '');
      styleEl.textContent = cachedStyles;
      shadowRoot.appendChild(styleEl);
      return;
    }

    if (styleEl.textContent !== cachedStyles) {
      styleEl.textContent = cachedStyles;
    }

    shadowRoot.appendChild(styleEl);
  }

  /**
   * @param {HTMLElement} host
   * @returns {Intl.NumberFormat}
   */
  function getFormatter(host) {
    return new Intl.NumberFormat(host.getAttribute('locale') || undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
  }

  /**
   * @param {string} text
   * @returns {number|null}
   */
  function parseFormattedPrice(text) {
    const num = parseFloat(String(text).replace(/[^0-9.-]/g, ''));
    return Number.isNaN(num) ? null : num;
  }

  /**
   * @param {ShadowRoot} shadow
   * @param {HTMLElement} host
   * @returns {object[]}
   */
  function getSlotSelections(shadow, host) {
    const variants = JSON.parse(host.getAttribute('variants') || '[]');
    const selectedVariant = JSON.parse(host.getAttribute('selected_variant') || 'null');
    const selectedBundle = shadow.querySelector('.dk-bundle-selected');

    if (!selectedBundle) {
      return selectedVariant ? [selectedVariant] : [];
    }

    const slots = selectedBundle.querySelectorAll('.dk-bundle-slot');
    if (slots.length === 0) {
      return selectedVariant ? [selectedVariant] : [];
    }

    const selections = [];
    slots.forEach((slot) => {
      const selects = slot.querySelectorAll('.dk-bundle-slot-selector-wrapper select');
      if (selects.length === 0) {
        if (selectedVariant) selections.push(selectedVariant);
        return;
      }

      const optionValues = Array.from(selects).map((select) => select.value);
      const variant =
        variants.find((v) => v.options.every((opt, index) => opt === optionValues[index])) ||
        selectedVariant;

      if (variant) selections.push(variant);
    });

    return selections.length > 0 ? selections : selectedVariant ? [selectedVariant] : [];
  }

  /**
   * @param {object[]} selections
   * @param {number} bundleSize
   * @param {number|undefined} tierIndex
   * @param {HTMLElement} host
   * @returns {number|null}
   */
  function calculateBundleTotal(selections, bundleSize, tierIndex, host) {
    if (!selections.length || bundleSize < 1) return null;

    const slots = [];
    for (let i = 0; i < bundleSize; i += 1) {
      slots.push(selections[i % selections.length]);
    }

    if (tierIndex === undefined || tierIndex === null || Number.isNaN(tierIndex)) {
      return slots[0].price / 100;
    }

    const getsType = host.getAttribute('gets_type');
    const conversionRate = parseFloat(host.getAttribute('conversion_rate') || '1');
    const discount = JSON.parse(host.getAttribute('discount') || '{"tiers":[]}');
    const tier = discount.tiers?.[tierIndex];

    if (!tier) return slots[0].price / 100;

    return slots.reduce((acc, slot) => {
      const price =
        getsType === 'percentage'
          ? (slot.price - slot.price * (tier.gets.value / 100)) / 100
          : (slot.price - tier.gets.value * conversionRate) / 100;
      return acc + price;
    }, 0);
  }

  /**
   * @param {object[]} selections
   * @param {number} bundleSize
   * @returns {number|null}
   */
  function calculateCompareAtTotal(selections, bundleSize) {
    if (!selections.length || bundleSize < 1) return null;

    let sum = 0;
    for (let i = 0; i < bundleSize; i += 1) {
      const variant = selections[i % selections.length];
      if (!variant?.compare_at_price || variant.compare_at_price <= 0) {
        return null;
      }
      sum += variant.compare_at_price / 100;
    }

    return sum;
  }

  /**
   * @param {object[]} selections
   * @param {number} bundleSize
   * @returns {number|null}
   */
  function calculateBaseTotal(selections, bundleSize) {
    const compareAtTotal = calculateCompareAtTotal(selections, bundleSize);
    if (compareAtTotal !== null) return compareAtTotal;

    if (!selections.length || bundleSize < 1) return null;

    let sum = 0;
    for (let i = 0; i < bundleSize; i += 1) {
      const variant = selections[i % selections.length];
      if (!variant?.price) return null;
      sum += variant.price / 100;
    }

    return sum;
  }

  /**
   * @param {object[]} selections
   * @param {number} bundleSize
   * @param {number|undefined} tierIndex
   * @param {HTMLElement} host
   * @returns {number|null}
   */
  function calculateSavingsPercentRaw(selections, bundleSize, tierIndex, host) {
    const baseTotal = calculateBaseTotal(selections, bundleSize);
    const discountedTotal = calculateBundleTotal(selections, bundleSize, tierIndex, host);

    if (baseTotal === null || discountedTotal === null || baseTotal <= 0) return null;

    return Math.round(((baseTotal - discountedTotal) / baseTotal) * 100);
  }

  /**
   * @param {object[]} selections
   * @param {number} bundleSize
   * @param {number|undefined} tierIndex
   * @param {HTMLElement} host
   * @returns {number|null}
   */
  function calculateSavingsPercent(selections, bundleSize, tierIndex, host) {
    const pct = calculateSavingsPercentRaw(selections, bundleSize, tierIndex, host);
    return pct !== null && pct > 0 ? pct : null;
  }

  /**
   * @param {HTMLElement} bundle
   * @param {number|null} total
   * @param {Intl.NumberFormat} formatter
   */
  function updatePricingElements(bundle, total, formatter) {
    const pricing = bundle.querySelector('.dk-bundle-pricing');
    if (!pricing || total === null) return;

    const bundleSize = parseInt(bundle.dataset.value, 10) || 1;
    const totalEl = pricing.querySelector('.dk-bundle-pricing-total');
    const eachEl = pricing.querySelector('.dk-bundle-pricing-each');
    const compareEl = pricing.querySelector('.dk-bundle-pricing-compare');

    if (totalEl) {
      const dkTotal = bundle.querySelector('.dk-bundle-total');
      const nextTotal = dkTotal?.textContent?.trim() || formatter.format(total);
      setTextIfChanged(totalEl, nextTotal);
    }

    if (eachEl) {
      const perBox = total / bundleSize;
      let priceEl = eachEl.querySelector('.dk-bundle-pricing-each-price');

      if (!priceEl) {
        eachEl.textContent = '';
        priceEl = document.createElement('span');
        priceEl.className = 'dk-bundle-pricing-each-price';
        eachEl.appendChild(priceEl);
        eachEl.append(' / Box');
      }

      setTextIfChanged(priceEl, formatter.format(perBox));
    }

    if (compareEl) {
      const host = shadowHostMap.get(bundle.getRootNode());
      if (host) {
        const selections = getSlotSelections(bundle.getRootNode(), host);
        const compareTotal = calculateCompareAtTotal(selections, bundleSize);
        setTextIfChanged(compareEl, compareTotal !== null ? formatter.format(compareTotal) : '');
      }
    }
  }

  /**
   * @param {ShadowRoot} shadow
   * @param {HTMLElement} host
   */
  function syncPricingFromDkTotal(shadow, host) {
    const formatter = getFormatter(host);
    const selections = getSlotSelections(shadow, host);
    const selectedBundle = shadow.querySelector('.dk-bundle-selected');
    const dkTotalEl = selectedBundle?.querySelector('.dk-bundle-total');
    const dkTotalText = dkTotalEl?.textContent?.trim();
    const dkTotalValue = dkTotalText ? parseFormattedPrice(dkTotalText) : null;

    shadow.querySelectorAll('.dk-bundle').forEach((bundle) => {
      const bundleSize = parseInt(bundle.dataset.value, 10) || 1;
      const tierAttr = bundle.dataset.tier;
      const tierIndex = tierAttr !== undefined ? parseInt(tierAttr, 10) : undefined;

      let total;
      if (bundle.classList.contains('dk-bundle-selected') && dkTotalValue !== null) {
        total = dkTotalValue;
      } else {
        total = calculateBundleTotal(selections, bundleSize, tierIndex, host);
      }

      updatePricingElements(bundle, total, formatter);
    });

    syncAtcButtonState(shadow);

    if (getEnhancerConfig()?.useCompareAtSavings) {
      enhanceCompareAtSavings(shadow, host);
      enhanceComplimentaryLabels(shadow);
    }
  }

  /**
   * @param {HTMLElement} bundleSelector
   */
  function promoteInputContainer(bundleSelector) {
    const inputContainer = bundleSelector.querySelector('.dk-bundle-input-container');
    if (!inputContainer) return;
    if (inputContainer.parentElement === bundleSelector) return;
    bundleSelector.prepend(inputContainer);
  }

  /**
   * @param {HTMLElement} bundleSelector
   */
  function ensurePricingColumn(bundleSelector) {
    const bundle = bundleSelector.closest('.dk-bundle');
    if (!bundle || bundleSelector.querySelector('.dk-bundle-pricing')) return;

    const pricing = document.createElement('div');
    pricing.className = 'dk-bundle-pricing';
    pricing.setAttribute('aria-hidden', 'true');
    pricing.innerHTML = `
      <div class="dk-bundle-pricing-total"></div>
      <div class="dk-bundle-pricing-row">
        <span class="dk-bundle-pricing-each"><span class="dk-bundle-pricing-each-price"></span> / Box</span>
        <span class="dk-bundle-pricing-compare"></span>
      </div>
    `;

    bundleSelector.appendChild(pricing);
  }

  /**
   * @param {ParentNode} root
   */
  function enhancePricing(root) {
    root.querySelectorAll('.dk-bundle-selector').forEach((bundleSelector) => {
      promoteInputContainer(bundleSelector);
      ensurePricingColumn(bundleSelector);
    });
  }

  /**
   * @param {ParentNode} root
   */
  function enhanceMostPopularFlag(root) {
    root.querySelectorAll('.dk-bundle-most-popular-flag').forEach((flag) => {
      if (flag.closest('.dk-bundle-most-popular-wrapper')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'dk-bundle-most-popular-wrapper';

      const label = document.createElement('span');
      label.className = 'dk-bundle-most-popular-flag';
      label.style.setProperty('--dynamic-value', '#830522');
      label.textContent = flag.textContent.trim();

      wrapper.appendChild(label);
      flag.replaceWith(wrapper);
    });
  }

  /**
   * @param {ShadowRoot} shadow
   */
  function syncAtcButtonState(shadow) {
    const dkButton = shadow.querySelector('.dk-bundle-selected .dk-bundle-add-to-cart');
    const themeButtons = document.querySelectorAll(
      'product-form.main-product-form .product-form__btns button[type="submit"][name="add"]'
    );

    themeButtons.forEach((btn) => {
      if (!dkButton) {
        if (btn.disabled !== true) btn.disabled = true;
        return;
      }
      if (btn.disabled !== dkButton.disabled) {
        btn.disabled = dkButton.disabled;
      }
    });
  }

  /**
   * @returns {HTMLElement|null}
   */
  function findBundleHostForForm() {
    const productInfo =
      document.querySelector('product-info') ||
      document.querySelector('.product__info') ||
      document.querySelector('.product__info-wrapper');

    if (!productInfo) {
      return document.querySelector(HOST_SELECTOR);
    }

    return productInfo.querySelector(HOST_SELECTOR);
  }

  function setupAtcProxy() {
    if (atcProxyInitialized) return;

    const host = findBundleHostForForm();
    if (!host) return;

    const mainForm = document.querySelector('product-form.main-product-form form');
    if (!mainForm || mainForm.getAttribute(ATC_PROXY_ATTR) === 'true') {
      if (mainForm) atcProxyInitialized = true;
      return;
    }

    mainForm.setAttribute(ATC_PROXY_ATTR, 'true');
    atcProxyInitialized = true;

    const variantInput = mainForm.querySelector('input[name="id"][data-product-form-variant]');
    if (variantInput) {
      variantInput.disabled = true;
      variantInput.setAttribute('data-bundle-disabled', 'true');
    }

    const triggerDkAddToCart = (event) => {
      const bundleHost = findBundleHostForForm();
      if (!bundleHost?.shadowRoot) return;

      const dkButton = bundleHost.shadowRoot.querySelector('.dk-bundle-selected .dk-bundle-add-to-cart');
      if (!dkButton || dkButton.disabled) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      dkButton.click();
      return false;
    };

    mainForm.addEventListener('submit', triggerDkAddToCart, { capture: true, passive: false });

    mainForm.querySelectorAll('button[type="submit"][name="add"]').forEach((btn) => {
      btn.addEventListener('click', triggerDkAddToCart, { capture: true, passive: false });
    });
  }

  /**
   * @param {ShadowRoot} shadow
   * @param {HTMLElement} host
   */
  function schedulePricingSync(shadow, host) {
    requestAnimationFrame(() => {
      runEnhancement(shadow, host, () => {
        syncPricingFromDkTotal(shadow, host);
      });
    });
  }

  /**
   * @param {Event} event
   */
  function handleStyleButtonClick(event) {
    const button = event.currentTarget;
    const value = button.dataset.value;
    const select = button.closest('.dk-bundle-slot-selector-wrapper')?.querySelector('select');

    if (!select) return;

    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    const buttons = button.parentElement?.querySelectorAll('.dk-style-btn');
    buttons?.forEach((btn) => btn.classList.remove('selected'));
    button.classList.add('selected');

    const shadow = button.getRootNode();
    const host = shadowHostMap.get(shadow);
    if (host) schedulePricingSync(shadow, host);
  }

  /**
   * @param {HTMLSelectElement} select
   */
  function convertSelectToButtons(select) {
    if (select.getAttribute(DATA_ATTR) === 'true') return;

    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'dk-style-selector';
    buttonGroup.setAttribute('role', 'group');
    buttonGroup.setAttribute('aria-label', select.getAttribute('aria-label') || 'Variant option');

    Array.from(select.options).forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dk-style-btn';
      button.dataset.value = option.value;
      button.textContent = option.textContent;

      if (option.selected) {
        button.classList.add('selected');
      }

      button.addEventListener('click', handleStyleButtonClick);
      buttonGroup.appendChild(button);
    });

    const shadow = select.getRootNode();
    const host = shadowHostMap.get(shadow);

    select.addEventListener('change', () => {
      if (host) schedulePricingSync(shadow, host);
    });

    select.setAttribute(DATA_ATTR, 'true');
    select.classList.add('dk-style-select--hidden');
    select.parentElement?.insertBefore(buttonGroup, select);
  }

  /**
   * @param {HTMLElement} bundle
   * @returns {HTMLElement}
   */
  function ensureCustomSavingsContainer(bundle) {
    let custom = bundle.querySelector('.dk-bundle-savings[data-dk-custom-savings]');
    if (custom) return custom;

    custom = document.createElement('div');
    custom.className = 'dk-bundle-savings';
    custom.setAttribute('data-dk-custom-savings', '');

    const value = document.createElement('span');
    value.className = 'dk-bundle-savings-value';
    custom.appendChild(value);

    const native = bundle.querySelector('.dk-bundle-savings:not([data-dk-custom-savings])');
    if (native) {
      native.insertAdjacentElement('afterend', custom);
    } else {
      const anchor =
        bundle.querySelector('.dk-bundle-info-wrapper') ||
        bundle.querySelector('.dk-bundle-info') ||
        bundle;
      anchor.appendChild(custom);
    }

    return custom;
  }

  /**
   * @param {ParentNode} root
   * @param {HTMLElement} host
   */
  function enhanceCompareAtSavings(root, host) {
    const config = getEnhancerConfig();
    if (!config?.useCompareAtSavings) return;

    const bundlesContainer = root.querySelector('.dk-bundles');
    if (bundlesContainer) {
      bundlesContainer.setAttribute('data-dk-compare-at-savings', '');
    }

    const selections = getSlotSelections(root, host);
    const bundles = root.querySelectorAll('.dk-bundle');
    let previousPct = null;

    bundles.forEach((bundle, index) => {
      const bundleSize = parseInt(bundle.dataset.value, 10) || 1;
      const tierAttr = bundle.dataset.tier;
      const tierIndex = tierAttr !== undefined ? parseInt(tierAttr, 10) : undefined;
      const rawPct = calculateSavingsPercentRaw(selections, bundleSize, tierIndex, host);
      const pct = rawPct !== null && rawPct > 0 ? rawPct : null;
      const custom = bundle.querySelector('.dk-bundle-savings[data-dk-custom-savings]');

      if (pct === null) {
        custom?.remove();
        previousPct = rawPct;
        return;
      }

      const savingsContainer = ensureCustomSavingsContainer(bundle);
      const savingsValue = savingsContainer.querySelector('.dk-bundle-savings-value');
      const showExtra = index > 0 && previousPct !== null && pct > previousPct;
      const nextText = showExtra ? `${pct}% OFF EXTRA` : `${pct}% OFF`;

      setTextIfChanged(savingsValue, nextText);
      previousPct = rawPct;
    });
  }

  /**
   * @param {ParentNode} root
   */
  function enhanceSavingsLabels(root) {
    if (getEnhancerConfig()?.useCompareAtSavings) return;

    root.querySelectorAll('.dk-bundle-savings-value').forEach((el) => {
      const text = el.textContent.trim();
      if (!text || text.toUpperCase().includes('EXTRA')) return;
      const nextText = `${text} EXTRA`;
      setTextIfChanged(el, nextText);
    });
  }

  /**
   * @param {ParentNode} root
   */
  function enhanceComplimentaryLabels(root) {
    const config = getEnhancerConfig();
    if (!config?.complimentaryLabel) return;

    const optionSet = new Set(parseOptionIndexes(config.complimentaryOptions));
    if (!optionSet.size) return;

    root.querySelectorAll('.dk-bundle').forEach((bundle, index) => {
      const optionNumber = index + 1;
      const savingsValue =
        bundle.querySelector('.dk-bundle-savings[data-dk-custom-savings] .dk-bundle-savings-value') ||
        bundle.querySelector('.dk-bundle-savings-value');
      const existingLabels = bundle.querySelectorAll('.dk-bundle-complimentary-label');

      if (!optionSet.has(optionNumber) || !savingsValue) {
        existingLabels.forEach((label) => label.remove());
        return;
      }

      let label = savingsValue.nextElementSibling;
      if (!label?.classList.contains('dk-bundle-complimentary-label')) {
        existingLabels.forEach((existingLabel) => existingLabel.remove());
        label = document.createElement('span');
        label.className = 'dk-bundle-complimentary-label';
        savingsValue.insertAdjacentElement('afterend', label);
      } else {
        existingLabels.forEach((existingLabel) => {
          if (existingLabel !== label) existingLabel.remove();
        });
      }

      setTextIfChanged(label, config.complimentaryLabel);
    });
  }

  /**
   * @param {ParentNode} root
   */
  function enhanceSlotLabels(root) {
    root.querySelectorAll('.dk-bundle-slots').forEach((slotsContainer) => {
      slotsContainer.querySelectorAll('.dk-bundle-slot').forEach((slot, index) => {
        if (slot.querySelector('.dk-slot-label')) return;

        const label = document.createElement('span');
        label.className = 'dk-slot-label';
        label.textContent = `Box ${index + 1}.`;

        const wrapper = slot.querySelector('.dk-bundle-slot-selector-wrapper');
        if (wrapper) {
          slot.insertBefore(label, wrapper);
        } else {
          slot.prepend(label);
        }
      });
    });
  }

  /**
   * @param {ShadowRoot} shadow
   * @param {HTMLElement} host
   */
  function enhanceAll(shadow, host) {
    enhanceSelects(shadow);
    enhanceMostPopularFlag(shadow);
    enhancePricing(shadow);
    syncPricingFromDkTotal(shadow, host);
  }

  /**
   * @param {ParentNode} root
   */
  function enhanceSelects(root) {
    root.querySelectorAll(SELECT_SELECTOR).forEach(convertSelectToButtons);
    enhanceSlotLabels(root);

    const host = shadowHostMap.get(root);
    if (getEnhancerConfig()?.useCompareAtSavings && host) {
      enhanceCompareAtSavings(root, host);
    } else {
      enhanceSavingsLabels(root);
    }

    enhanceComplimentaryLabels(root);
  }

  /**
   * @param {HTMLElement} host
   */
  async function enhanceHost(host) {
    const shadow = host.shadowRoot;
    if (!shadow) return;

    shadowHostMap.set(shadow, host);
    await injectStyles(shadow);

    if (initializedShadows.has(shadow)) {
      runEnhancement(shadow, host, () => {
        enhanceAll(shadow, host);
      });
      setupAtcProxy();
      return;
    }

    runEnhancement(shadow, host, () => {
      enhanceAll(shadow, host);
    });
    setupAtcProxy();

    const observer = new MutationObserver(() => {
      runEnhancement(shadow, host, () => {
        enhanceAll(shadow, host);
      });
    });

    observer.observe(shadow, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    initializedShadows.add(shadow);
    host.setAttribute(HOST_INIT_ATTR, 'true');
  }

  function scanAndEnhance() {
    document.querySelectorAll(HOST_SELECTOR).forEach((host) => {
      enhanceHost(host).catch((error) => {
        console.error('Discount Kit enhancer: failed to enhance bundle-selector', error);
      });
    });
    setupAtcProxy();
  }

  /**
   * @param {MutationRecord[]} mutations
   */
  function handleBodyMutations(mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const element = /** @type {Element} */ (node);
        if (element.matches?.(HOST_SELECTOR) || element.querySelector?.(HOST_SELECTOR)) {
          scanAndEnhance();
          return;
        }
      }
    }
  }

  async function init() {
    if (customElements.get(HOST_SELECTOR)) {
      scanAndEnhance();
    } else {
      try {
        await customElements.whenDefined(HOST_SELECTOR);
      } catch (error) {
        console.error('Discount Kit enhancer: bundle-selector custom element unavailable', error);
        return;
      }
      scanAndEnhance();
    }

    const bodyObserver = new MutationObserver(handleBodyMutations);
    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
